// src/pages/healthcare/appointments/components/StaffLegend.tsx
import React from "react";
import { Badge, Button, Empty } from "antd";
import SectionCard from "../../../../components/shared/SectionCard";
import PractitionerAvatar from "../../../../components/shared/PractitionerAvatar";
import { UserOutlined } from "@ant-design/icons";
import { TeamMember } from "../../../../types/admin/TeamTypes";
import { practitionerColour } from "../../../../utils/appointments/practitionerColour";
import type { UserUuid } from "../../../../types/identity";

interface Event {
	id: number;
	title: string;
	start: Date;
	end: Date;
	resource: {
		appointment: any;
	};
}

interface StaffLegendProps {
	teamMembers: TeamMember[];
	appointments: Event[];
	visibleIds: Set<UserUuid>;
	onToggleVisible: (id: UserUuid) => void;
	onShowAll: () => void;
	onShowNone: () => void;
	onShowOnlyMine: () => void;
	onlyMineUnavailable?: boolean;
}

const StaffLegend: React.FC<StaffLegendProps> = ({ teamMembers, appointments, visibleIds, onToggleVisible, onShowAll, onShowNone, onShowOnlyMine, onlyMineUnavailable }) => {
	const getAppointmentCount = (staffId: UserUuid) => {
		return appointments.filter((apt) => {
			const appt = apt.resource.appointment;
			if (appt.practitioner_id === staffId) return true;
			if (appt.practitioners?.some((p: any) => p.practitioner_id === staffId)) return true;
			return false;
		}).length;
	};

	return (
		<SectionCard
		title="Team Members"
		extra={<span style={{ color: "var(--mg-text-secondary)" }}>Tick to show</span>}
	>
			<div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
				<Button size="small" onClick={onShowAll} data-testid="appointments-staff-all">All</Button>
				<Button size="small" onClick={onShowNone} data-testid="appointments-staff-none">None</Button>
				<Button size="small" onClick={onShowOnlyMine} data-testid="appointments-staff-only-me">Only me</Button>
			</div>
			{onlyMineUnavailable && (
				<div data-testid="appointments-staff-only-me-notice" style={{ fontSize: 11, color: "var(--mg-text-secondary)", marginBottom: 8 }}>
					You are not on the practitioner roster — showing everyone.
				</div>
			)}
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 8,
					maxHeight: "80vh",
					overflowY: "auto",
				}}
			>
				{teamMembers.length === 0 ? (
					<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No team members" style={{ padding: "32px 0" }} />
				) : (
					teamMembers.map((member) => {
						const color = practitionerColour(member);
						const count = getAppointmentCount(member.id);
						const isVisible = visibleIds.has(member.id);

						return (
							<div
								key={member.id}
								onClick={() => onToggleVisible(member.id)}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									padding: 8,
									borderRadius: 6,
									cursor: "pointer",
									transition: "all 0.2s",
									backgroundColor: "transparent",
									opacity: isVisible ? 1 : 0.45,
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.backgroundColor = "var(--mg-bg-subtle)";
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.backgroundColor = "transparent";
								}}
							>
								<div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, overflow: "hidden" }}>
									{/* Native input so the tick can carry the practitioner's own column colour. */}
									<input
										type="checkbox"
										checked={isVisible}
										onChange={() => onToggleVisible(member.id)}
										// The row's onClick does the same toggle now, so an unstopped click would fire both and cancel itself out.
										onClick={(e) => e.stopPropagation()}
										aria-label={`Show ${member.first_name} ${member.last_name}`}
										data-testid={`appointments-staff-toggle-${member.id}`}
										style={{ accentColor: color, width: 14, height: 14, flexShrink: 0, cursor: "pointer" }}
									/>
									<PractitionerAvatar
										member={member}
										size={32}
										style={{ boxShadow: `0 2px 4px ${color}40` }}
										data-testid={`appointments-staff-legend-avatar-${member.id}`}
									/>
									<div style={{ flex: 1, overflow: "hidden" }}>
										<div
											style={{
												fontSize: 12,
												fontWeight: 500,
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
										>
											{member.first_name} {member.last_name}
										</div>
										{count > 0 && (
											<div style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>
												{count} appointment{count !== 1 ? "s" : ""}
											</div>
										)}
									</div>
								</div>
								<Badge
									count={count}
									style={{
										backgroundColor: count > 0 ? color : "var(--mg-border-strong)",
										fontSize: 10,
										minWidth: 24,
									}}
								/>
							</div>
						);
					})
				)}
			</div>
		</SectionCard>
	);
};

export default StaffLegend;
